import { Button, Form, Modal, Radio, Spin } from 'antd';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import doNothing from '../../../../../../helpers/doNothing';
import queries from '../../../../queries/queries';
import useCounterpartiesTypes from '../../../../queries/useCounterpartiesTypes';
import createCounterparty from '../../../../requests/createCounterparty';
import createCounterpartyAttachments from '../../../../requests/createCounterpartyAttachments';
import editCounterparty from '../../../../requests/editCounterparty';
import transformContractorData from '../../../../utils/transformContractorData';
import ContractorModalOrganisationMode from '../ContractorModalOrganisationMode/ContractorModalOrganisationMode';
import ContractorModalPersonMode from '../ContractorModalPersonMode/ContractorModalPersonMode';
import SecondStep from '../SecondStep/SecondStep';
import { contractorType } from './ContractorModal.module.scss';

const ContractorModalNode = ({
  visible,
  onCancel,
  form,
  editingCounterparty,
}) => {
  const [loading, setLoading] = useState(false);
  const initialType = useMemo(() => {
    if (!editingCounterparty) {
      return 'person';
    } else {
      return editingCounterparty.type?.name === 'физическое лицо'
        ? 'person'
        : 'organisation';
    }
  }, [editingCounterparty]);
  const queryClient = useQueryClient();
  const [chosenContractorType, setChosenContractorType] = useState(
    () => initialType
  );
  const [step, setStep] = useState(1);
  const [fileList, setFileList] = useState(
    () =>
      editingCounterparty?.attachments?.map((file) => {
        file.uid = file.id;
        return file;
      }) || []
  );
  const [firstStepFormData, setFirstStepFormData] = useState(null); // null || object
  const [secondStepFormData, setSecondStepFormData] = useState(null); // null || object
  const { data: counterpartiesTypes } = useCounterpartiesTypes();

  useEffect(() => {
    if (step === 1) {
      if (!firstStepFormData) {
        form.setFieldsValue({
          contractorType: chosenContractorType,
        });
      } else {
        form.setFieldsValue({
          ...firstStepFormData,
        });
      }
    } else if (step === 2) {
      if (!firstStepFormData) {
        form.setFieldsValue({
          contractorType: chosenContractorType,
        });
      } else {
        form.setFieldsValue({
          ...secondStepFormData,
        });
      }
    }
  }, [
    step,
    chosenContractorType,
    firstStepFormData,
    secondStepFormData,
    form.setFieldsValue,
  ]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setLoading(true);
      form.validateFieldsAndScroll(async (err, values) => {
        const transformedData = transformContractorData(
          {
            ...firstStepFormData,
            ...secondStepFormData,
            ...values,
          },
          counterpartiesTypes,
          editingCounterparty
        );

        if (!err) {
          if (editingCounterparty) {
            await editCounterparty({
              ...transformedData,
              attachments: fileList.filter((file) => file.id),
              Id: editingCounterparty.id,
            });
            const newFiles = fileList.filter((file) => file instanceof File);
            await createCounterpartyAttachments(
              editingCounterparty.id,
              newFiles
            );
          } else {
            const id = await createCounterparty(transformedData);

            if (id && fileList.length > 0) {
              await createCounterpartyAttachments(id, fileList);
            }
          }
          queryClient.invalidateQueries(queries.counterparties);
          setLoading(false);
          onCancel();
        }
      });
    },
    [
      fileList,
      queryClient,
      firstStepFormData,
      secondStepFormData,
      onCancel,
      form,
      editingCounterparty,
      counterpartiesTypes,
    ]
  );

  const clickNext = (e) => {
    e.preventDefault();
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        setFirstStepFormData(values);
        setStep(2);
      }
    });
  };
  const clickBack = (e) => {
    e.preventDefault();
    setSecondStepFormData(form.getFieldsValue());
    setStep(1);
  };
  const changeContractorType = (e) => {
    setChosenContractorType(e.target.value);
  };
  const getOptions = (message) => {
    return {
      rules: [
        {
          required: true,
          message,
        },
      ],
    };
  };
  return (
    <Modal
      title={`${
        editingCounterparty ? 'Редактировать' : 'Добавить'
      } контрагента`}
      visible={visible}
      onOk={handleSubmit}
      onCancel={loading ? doNothing : onCancel}
      width={650}
      footer={
        step === 1 ? (
          <div>
            <Button onClick={onCancel} disabled={loading}>
              Отмена
            </Button>
            <Button type={'primary'} onClick={clickNext}>
              Далее
            </Button>
          </div>
        ) : (
          <div>
            <Button onClick={onCancel} disabled={loading}>
              Отмена
            </Button>
            <Button type={'primary'} onClick={clickBack} disabled={loading}>
              Назад
            </Button>
            <Button type={'primary'} onClick={handleSubmit} disabled={loading}>
              {editingCounterparty ? 'Редактировать' : 'Добавить'} контрагента
            </Button>
          </div>
        )
      }
    >
      <Spin spinning={loading}>
        <Form layout="vertical" onSubmit={handleSubmit}>
          {step === 1 && (
            <>
              <Form.Item>
                {form.getFieldDecorator('contractorType', {
                  initialValue: initialType,
                })(
                  <Radio.Group size="large" onChange={changeContractorType}>
                    <Radio.Button value="person" className={contractorType}>
                      Физическое лицо
                    </Radio.Button>
                    <Radio.Button
                      value="organisation"
                      className={contractorType}
                    >
                      Организация
                    </Radio.Button>
                  </Radio.Group>
                )}
              </Form.Item>
              {chosenContractorType === 'person' ? (
                <ContractorModalPersonMode
                  editingCounterparty={editingCounterparty}
                  setFieldsValue={form.setFieldsValue}
                  getFieldValue={form.getFieldValue}
                  getOptions={getOptions}
                  getFieldDecorator={form.getFieldDecorator}
                />
              ) : (
                <ContractorModalOrganisationMode
                  editingCounterparty={editingCounterparty}
                  setFieldsValue={form.setFieldsValue}
                  getFieldValue={form.getFieldValue}
                  getFieldDecorator={form.getFieldDecorator}
                  getOptions={getOptions}
                />
              )}
            </>
          )}
          {step !== 1 && (
            <SecondStep
              setFieldsValue={form.setFieldsValue}
              getFieldValue={form.getFieldValue}
              getFieldDecorator={form.getFieldDecorator}
              editingCounterparty={editingCounterparty}
              fileList={fileList}
              setFileList={setFileList}
            />
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

const ContractorModal = Form.create({ name: 'add_new_contractor' })(
  ContractorModalNode
);

export default memo(ContractorModal);
